<script lang="ts">
  import { onDestroy, onMount, tick } from "svelte";
  import type { ChildExplore, ChildGroup, Explore, Group } from "../interfaces/explore";
  import { getHash, LOCAL_STORAGE_KEYS, setHash, themes } from "../services/shared";
  import Input from "./input.svelte";

  const navigationIds = {
    header: 'navigation_header',
    search: 'navigation_search',
    items: 'navigation_items',
    selectedItem: 'navigation_items_item--selected'
  };

  export let explore: Explore | null = null;
  export let groups: Group[] = [];
  export let endpointGroups: ChildExplore[] = [];

  export let anchor: string | null = '';
  $: {
    if (hasClickedExpand) {
      hasClickedExpand = false;
    } else {
      const currentGroup = groups.find(g => g.name === anchor?.split('/')[0])?.name;
      if (currentGroup && !expandedgroups.includes(currentGroup)) {
        expandedgroups = [...expandedgroups, currentGroup];
      }
    }
  }

  export let setScrollPosition = (anchor: string) => null;
  export let toggledExpandEndpointGroup = (group: any) => null;

  let preventScroll = false;
  let hasClickedExpand = false;

  let navigationElement: Element | null = null;

  let expandedgroups: string[] = [];
  let skeleton = [];

  let animationTimeout: any = null;
  let mobileOpenerElement: HTMLElement | null = null;
  let isNavigationVisible = false;
  $: {
    const htmlElement = document.getElementsByTagName('html')[0];
    if (htmlElement) {
      const scrollBarWidth = window.innerWidth - document.body.clientWidth;
      htmlElement.style.setProperty('--scrollbarWidth', scrollBarWidth + 'px');

      const className = 'overflowHidden';
      isNavigationVisible ? htmlElement.classList.add(className) : htmlElement.classList.remove(className);
    }

    if (mobileOpenerElement) {
      if (animationTimeout) { clearTimeout(animationTimeout); }
      animationTimeout = setTimeout(() => mobileOpenerElement.style.transition = isNavigationVisible ? 'unset' : 'transform 200ms ease', 200);
    }
  }

  let isSearchFocused = false;
  let searchValue = '';
  $: {
    // TODO search logic (via query param)
  }

  const switchTheme = () => {
    const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const bodyClassList = document.body.classList;
    let foundTheme = '';
    let newTheme = '';

    for (const theme of themes) {
      if ([...bodyClassList].find(entry => entry === 'theme--' + theme)) {
        foundTheme = theme;
        break;
      }
    }

    if (foundTheme) {
      bodyClassList.remove('theme--' + foundTheme);
      newTheme = themes.find(theme => theme !== foundTheme);
    } else if (isDarkMode) {
      newTheme = 'light';
    } else {
      newTheme = 'dark';
    }

    localStorage.setItem(LOCAL_STORAGE_KEYS.THEME, newTheme);
    bodyClassList.add('theme--' + newTheme);
  };

  const fillSkeleton = () => {
    for (let i = 0; i < 20; i++) { skeleton.push(i); }
    skeleton = [...skeleton];
  };

  const onNavigationWheel = (event: any) => {
    const wheelDeltaY = event.wheelDeltaY;
    const scrollTop = navigationElement.scrollTop;
    const scrollDistance = Math.round(scrollTop + navigationElement.clientHeight);
    const navigationHeight = navigationElement.scrollHeight;

    if (
      scrollTop === 0 && wheelDeltaY > 0 ||
      scrollDistance <= navigationHeight + 2 && scrollDistance >= navigationHeight - 2 && wheelDeltaY < 0
    ) { event.preventDefault(); }
  };

  const onScroll = () => {
    if (preventScroll) {
      preventScroll = false;
    } else {
      if (anchor && navigationElement) {
        const navigationChildren = [...navigationElement.children];
        const itemsChildren = [...navigationChildren.find(child => child.id === navigationIds.items).children];
        const selectedItemElement = itemsChildren.find(child => child.id === navigationIds.selectedItem);

        if (selectedItemElement) {
          const selectedItemRect = selectedItemElement.getBoundingClientRect();
          if (selectedItemRect.bottom >= window.innerHeight || selectedItemRect.top <= 0) {
            const headerElement = navigationChildren.find(child => child.id === navigationIds.header);
            const searchElement = navigationChildren.find(child => child.id === navigationIds.search);
            let scrollY = (headerElement ? headerElement.getBoundingClientRect().height : 0) + (searchElement ? searchElement.getBoundingClientRect().height : 0);

            for (let i = 0; i < itemsChildren.length; i++) {
              const navigationItemElement = itemsChildren[i];
              if (navigationItemElement) {
                if (selectedItemRect.bottom >= window.innerHeight) {
                  scrollY += navigationItemElement.getBoundingClientRect().height;
                }

                if (navigationItemElement.id === navigationIds.selectedItem) {
                  break;
                } else if (selectedItemRect.top <= 0) {
                  scrollY += navigationItemElement.getBoundingClientRect().height;
                }
              }
            }

            if (selectedItemRect.bottom >= window.innerHeight) {
              scrollY -= window.innerHeight;
            }

            tick().then(() => navigationElement.scrollTo(0, scrollY));
          }
        }
      } else {
        navigationElement.scrollTo(0, 0);
      }
    }
  }

  const toggleExpanded = (group: Group) => {
    hasClickedExpand = true;
    preventScroll = true;

    if (expandedgroups.includes(group.name)) {
      expandedgroups.splice(expandedgroups.indexOf(group.name), 1);
    } else {
      expandedgroups.push(group.name);
    }

    expandedgroups = [...expandedgroups];
  };

  const onClickPixxioLogo = () => {
    if (getHash()) {
      anchor = null;
      setHash(anchor);
    }

    setScrollPosition(null);
    isNavigationVisible = false;
  };

  const onClickGroupParent = (group: Group) => {
    if (anchor !== group.name) {
      anchor = group.name;
      setHash(anchor);
    } else {
      isNavigationVisible = false;
    }

    setScrollPosition(group.name);
  };
  
  const onClickGroupChild = (group: Group, endpoint: ChildGroup) => {
    const mergedName = group.name + '/' + endpoint.name;
    if (anchor !== mergedName) {
      anchor = mergedName;
      setHash(anchor);
    }

    setScrollPosition(mergedName);
    isNavigationVisible = false;
  };

  const onClickExpander = (event: Event, group: Group) => {
    event.stopPropagation();
    toggleExpanded(group);
    toggledExpandEndpointGroup(group);
  };

  onMount(() => {
    fillSkeleton();
    document.addEventListener('scroll', onScroll, { passive: true });
  });

  onDestroy(() => {
    document.removeEventListener('scroll', onScroll);
  });
</script>

<div class="backdrop {isNavigationVisible ? '' : 'backdrop--hidden'}" on:click={() => isNavigationVisible = !isNavigationVisible}></div>

<div
  bind:this="{navigationElement}"
  class="navigation scrollbarOpacityTransition {isNavigationVisible ? '' : 'navigation--hidden'}"
  id="navigation__scroll"
  style={groups.length > 0 ? null : 'overflow: hidden;'}
  on:wheel={onNavigationWheel}
>
  <div class="navigation__header" id="{navigationIds.header}">
    <div class="navigation__header__headline pt-24 pr-24 pb-24 pl-24">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 25.78"
        fill="var(--grey-800)"
        on:click={onClickPixxioLogo}
      >
        <path
          d="M41.34 6.14a5.72 5.72 0 00-3.37-.97 5.54 5.54 0 00-3.33.97 4.9 4.9 0 00-1.07 1 1.58 1.58 0 00-1.58-1.59h-.18a.3.3 0 00-.3.31V24a.3.3 0 00.3.31h.42a1.61 1.61 0 001.61-1.61v-4.37a5.18 5.18 0 00.8.72 5.56 5.56 0 003.33 1 5.72 5.72 0 003.37-.96 6.59 6.59 0 002.17-2.65 8.96 8.96 0 00.76-3.79 8.85 8.85 0 00-.76-3.78 6.37 6.37 0 00-2.17-2.72zm0 9.15a4.11 4.11 0 01-1.37 1.89 3.6 3.6 0 01-2.3.69 3.63 3.63 0 01-3.69-2.58 8.39 8.39 0 01-.44-2.75 7.57 7.57 0 01.44-2.76 4.2 4.2 0 011.34-1.85 3.63 3.63 0 012.28-.66 3.96 3.96 0 012.37.69 4.3 4.3 0 011.38 1.9 7.32 7.32 0 01.44 2.68 7.45 7.45 0 01-.45 2.75z"
        />
        <path
          d="M47.44 19.9h1.69a.3.3 0 00.31-.3V6.18a.3.3 0 00-.31-.3h-1.69a.3.3 0 00-.31.3V19.6a.3.3 0 00.31.3z"
        />
        <path
          d="M81.31 15.38h1.7a.3.3 0 00.3-.31V6.26a.3.3 0 00-.3-.31h-1.7a.3.3 0 00-.3.31v8.8a.3.3 0 00.3.32z"
        />
        <path
          d="M64.36 5.84h-1.97a.31.31 0 00-.27.14L58.4 11l-3.75-5.02a.44.44 0 00-.24-.14h-1.96a.31.31 0 00-.24.51l4.89 6.44-5 6.6a.32.32 0 00.25.52h1.96a.23.23 0 00.24-.14l3.85-5.2 3.86 5.23a.42.42 0 00.27.14h1.96a.31.31 0 00.24-.52l-4.99-6.6 4.89-6.44a.35.35 0 00-.27-.54z"
        />
        <path
          d="M77.98 5.84h-1.96a.31.31 0 00-.28.14L72.03 11l-3.75-5.02a.44.44 0 00-.24-.14h-1.97a.31.31 0 00-.24.51l4.89 6.44-5.02 6.6a.32.32 0 00.24.52h1.96a.23.23 0 00.24-.14l3.85-5.2 3.86 5.2a.42.42 0 00.27.14h1.96a.31.31 0 00.24-.52l-4.99-6.6 4.89-6.44a.31.31 0 00-.24-.51z"
        />
        <path
          d="M99.2 9.04a6.16 6.16 0 00-2.33-2.62 6.94 6.94 0 00-3.65-.96 6.68 6.68 0 00-3.58.93A6.26 6.26 0 0087.27 9a8.63 8.63 0 00-.83 3.9 8.58 8.58 0 00.83 3.84 6.4 6.4 0 002.34 2.62 7.37 7.37 0 007.22.03 6.16 6.16 0 002.34-2.61 8.63 8.63 0 00.83-3.89 8.05 8.05 0 00-.8-3.85zm-2.71 7.6a3.77 3.77 0 01-3.24 1.44 3.86 3.86 0 01-3.23-1.44 6.21 6.21 0 01-1.1-3.82 7.37 7.37 0 01.48-2.72 3.8 3.8 0 011.41-1.82 4.06 4.06 0 012.41-.66 3.84 3.84 0 013.23 1.41 6.1 6.1 0 011.07 3.79 5.92 5.92 0 01-1.03 3.82z"
        />
        <path
          style="fill: var(--primary-500)"
          d="M81.31 19.77h1.7a.3.3 0 00.3-.31v-1.68a.3.3 0 00-.3-.31h-1.7a.3.3 0 00-.3.3v1.7a.3.3 0 00.3.3z"
        />
        <path
          style="fill: var(--primary-500)"
          d="M13.18 25.78a13.48 13.48 0 01-6.75-1.82l-.68-.4v-3.4H2.27l-.41-.67A12.76 12.76 0 010 12.9 13.06 13.06 0 0113.18 0a13.06 13.06 0 0113.18 12.9 13.06 13.06 0 01-13.18 12.88zm-4.61-3.8a10.27 10.27 0 004.6 1.04A10.26 10.26 0 0023.54 12.9 10.23 10.23 0 0013.18 2.8 10.26 10.26 0 002.82 12.92a10.13 10.13 0 001.07 4.51h1.86v-4.5a7.37 7.37 0 017.43-7.28 7.37 7.37 0 017.43 7.27 7.37 7.37 0 01-7.43 7.27H8.57zm4.6-13.56a4.56 4.56 0 00-4.6 4.5v4.52h4.6a4.51 4.51 0 100-9.02z"
        />
      </svg>
      <h4 on:click={onClickPixxioLogo}>API {#if explore?.pixxioVersion}&middot; {explore.pixxioVersion} {/if}</h4>
      <span></span>
      <div on:click="{switchTheme}" class="themeSwitch">
        <!-- 🌣 -->
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 14 14" fill="none">
          <path
            d="M6.99967 4.08333C5.38967 4.08333 4.08301 5.39 4.08301 7C4.08301 8.61 5.38967 9.91666 6.99967 9.91666C8.60968 9.91666 9.91634 8.61 9.91634 7C9.91634 5.39 8.60968 4.08333 6.99967 4.08333ZM1.16634 7.58333H2.33301C2.65384 7.58333 2.91634 7.32083 2.91634 7C2.91634 6.67916 2.65384 6.41666 2.33301 6.41666H1.16634C0.845508 6.41666 0.583008 6.67916 0.583008 7C0.583008 7.32083 0.845508 7.58333 1.16634 7.58333ZM11.6663 7.58333H12.833C13.1538 7.58333 13.4163 7.32083 13.4163 7C13.4163 6.67916 13.1538 6.41666 12.833 6.41666H11.6663C11.3455 6.41666 11.083 6.67916 11.083 7C11.083 7.32083 11.3455 7.58333 11.6663 7.58333ZM6.41634 1.16666V2.33333C6.41634 2.65416 6.67884 2.91666 6.99967 2.91666C7.32051 2.91666 7.58301 2.65416 7.58301 2.33333V1.16666C7.58301 0.845828 7.32051 0.583328 6.99967 0.583328C6.67884 0.583328 6.41634 0.845828 6.41634 1.16666ZM6.41634 11.6667V12.8333C6.41634 13.1542 6.67884 13.4167 6.99967 13.4167C7.32051 13.4167 7.58301 13.1542 7.58301 12.8333V11.6667C7.58301 11.3458 7.32051 11.0833 6.99967 11.0833C6.67884 11.0833 6.41634 11.3458 6.41634 11.6667ZM3.49384 2.67166C3.26634 2.44416 2.89301 2.44416 2.67134 2.67166C2.44384 2.89916 2.44384 3.2725 2.67134 3.49416L3.28967 4.1125C3.51717 4.34 3.89051 4.34 4.11217 4.1125C4.33384 3.885 4.33967 3.51166 4.11217 3.28999L3.49384 2.67166ZM10.7097 9.8875C10.4822 9.66 10.1088 9.66 9.88718 9.8875C9.65968 10.115 9.65968 10.4883 9.88718 10.71L10.5055 11.3283C10.733 11.5558 11.1063 11.5558 11.328 11.3283C11.5555 11.1008 11.5555 10.7275 11.328 10.5058L10.7097 9.8875ZM11.328 3.49416C11.5555 3.26666 11.5555 2.89333 11.328 2.67166C11.1005 2.44416 10.7272 2.44416 10.5055 2.67166L9.88718 3.28999C9.65968 3.51749 9.65968 3.89083 9.88718 4.1125C10.1147 4.33416 10.488 4.34 10.7097 4.1125L11.328 3.49416ZM4.11217 10.71C4.33967 10.4825 4.33967 10.1092 4.11217 9.8875C3.88467 9.66 3.51134 9.66 3.28967 9.8875L2.67134 10.5058C2.44384 10.7333 2.44384 11.1067 2.67134 11.3283C2.89884 11.55 3.27217 11.5558 3.49384 11.3283L4.11217 10.71Z"
            fill="var(--primary-500)"
          />
        </svg>
        <!-- ☽ -->
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
          <path
            d="M6.37 2.51C6.19 3.15 6.1 3.82 6.1 4.5C6.1 8.58 9.42 11.9 13.5 11.9C14.18 11.9 14.85 11.81 15.49 11.63C14.45 14.19 11.93 16 9 16C5.14 16 2 12.86 2 9C2 6.07 3.81 3.55 6.37 2.51ZM9 0C4.03 0 0 4.03 0 9C0 13.97 4.03 18 9 18C13.97 18 18 13.97 18 9C18 8.54 17.96 8.08 17.9 7.64C16.92 9.01 15.32 9.9 13.5 9.9C10.52 9.9 8.1 7.48 8.1 4.5C8.1 2.69 8.99 1.08 10.36 0.0999999C9.92 0.0399999 9.46 0 9 0Z"
            fill="var(--primary-500)"
          />
        </svg>
      </div>
    </div>
  </div>

  <!-- <div class="navigation__search {searchValue || isSearchFocused ? 'navigation__search--sticky' : ''} pb-24" id="{navigationIds.search}">
    <Input
      bind:value={searchValue}
      placeHolder="Search"
      focus="{() => isSearchFocused = true}"
      blur="{() => isSearchFocused = false}"
    />
  </div> -->

  <div class="navigation__items" id="{navigationIds.items}">
    {#if groups.length > 0}
      {#each groups as group}
        <div
          class="
            navigation__items__group groupItem
            {anchor === group.name || anchor?.split('/')[0] === group.name && anchor !== group.name && !expandedgroups.includes(group.name) ? 'groupItem--selected' : ''}
            {expandedgroups.includes(group.name) ? 'groupItem--expanded' : ''}
            pr-24 pl-24
          "
          id={(anchor === group.name ? navigationIds.selectedItem : null)}
          draggable="false"
          on:click={() => onClickGroupParent(group)}
        >
          <span>{group.name}</span>
          <div class="groupItem__itemWrapper" on:click={(event) => onClickExpander(event, group)}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="var(--grey-600)">
              <path d="M24 30.75 12 18.75 14.15 16.6 24 26.5 33.85 16.65 36 18.8Z"/>
            </svg>
          </div>
        </div>

        {#if expandedgroups.includes(group.name) && endpointGroups.find(eg => eg.name === group.name)?.groups.length > 0}
          {#each endpointGroups.find(eg => eg.name === group.name).groups as endpoint}
            <div
              class="
                navigation__items__group groupItemChild
                {(anchor === (group.name + '/' + endpoint.name) ? 'groupItemChild--selected' : '')}
                pr-24 pl-24
              "
              id={(anchor === (group.name + '/' + endpoint.name) ? navigationIds.selectedItem : null)}
              draggable="false"
              on:click={() => onClickGroupChild(group, endpoint)}
            >
              <div class="chip" style="background-color: var(--{endpoint.links[0]?.method?.toLowerCase()})">
                {#if endpoint.links[0]?.method}
                  {endpoint.links[0].method === 'DELETE' ? 'DEL' : endpoint.links[0].method}
                {:else}
                  &middot;
                {/if}
              </div>
              <span>{endpoint.name}</span>
            </div>
          {/each}
        {/if}
      {/each}
    {:else}
      {#each skeleton as item}
        <div class="navigation__items__loadingItem pulse mr-24 ml-24"></div>
      {/each}
    {/if}
  </div>
</div>

<div
  bind:this={mobileOpenerElement}
  class="mobileOpener {isNavigationVisible ? '' : 'mobileOpener--closed'}"
  on:click={() => isNavigationVisible = !isNavigationVisible}
>
  <div class="mobileOpener__itemWrapper">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="var(--primary-500)">
      <path d="M24 30.75 12 18.75 14.15 16.6 24 26.5 33.85 16.65 36 18.8Z"/>
    </svg>
  </div>
</div>

<style lang="scss">
  @import '../styles/variables.scss';
  @import '../styles/breakpoints.scss';

  .backdrop,
  .navigation,
  .mobileOpener {
    position: fixed;
    top: 0;
    bottom: 0;
    user-select: none;
  }

  .backdrop {
    right: 0;
    left: 0;
    background-color: var(--grey-900);
    will-change: opacity, visibility;
  }

  .navigation,
  .mobileOpener {
    height: 100%;
    background-color: var(--grey-100);
    will-change: transform;
  }

  .navigation {
    width: $navigationWidth;
    transition: transform 200ms ease, border-color 140ms;

    &__header {
      &__headline {
        display: flex;

        > svg,
        > h4 {
          cursor: pointer;
        }

        > svg {
          width: 100%;
          max-width: 110px;
        }

        h4 {
          color: var(--grey-500);
          padding: 12px 0 0 12px;
        }

        > span {
          flex: 1;
        }
        
        .themeSwitch {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 21px;
          margin-top: 4px;
          cursor: pointer;
        }
      }
    }

    // &__search {
    //   padding-right: 20px;
    //   padding-left: 20px;

    //   &--sticky {
    //     position: sticky;
    //     top: 18px;
    //   }
    // }

    &__items {
      &__group {
        &:last-child {
          margin-bottom: 24px;
        }

        &.groupItem {
          text-transform: capitalize;
        }
      }

      &__loadingItem {
        min-height: 21px;
        margin-top: 11px;
        margin-bottom: 23px;
        border-radius: 4px;
        opacity: .5;
      }
    }
  }

  .mobileOpener {
    transform: translateX($navigationWidth);
    display: flex;
    align-items: center;
    width: 34px;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 12px 0 18px -10px var(--grey-50);

    &:not(&--closed) {
      box-sizing: border-box;
      border: 1px solid var(--grey-300);
      border-top: none;
      border-bottom: none;
    }

    &--closed {
      transform: translateX(0);
      // box-shadow: 0 0 8px 0 var(--grey-50);

      .mobileOpener__itemWrapper svg {
        transform: rotate(270deg);
      }
    }

    &__itemWrapper {
      flex: 1;
      display: flex;
      margin: 0 5px;

      svg {
        transform: rotate(90deg);
      }
    }
  }

  .backdrop,
  .mobileOpener {
    display: none;
  }
  
  @media #{$mediaMd} {
    .backdrop,
    .mobileOpener {
      display: flex;
    }

    .backdrop {
      visibility: visible;
      opacity: .2;
      transition: visibility 200ms, opacity 200ms ease;

      &--hidden {
        visibility: hidden;
        opacity: 0;
      }
    }

    .navigation {
      &--hidden {
        transform: translateX(-$navigationWidth);
      }
    }
  }

  @media screen and (max-width: 354px) {
    .navigation {
      max-width: calc(100% - 34px);
    }
    .mobileOpener {
      transform: translateX(calc(100vw - 34px));

      &--closed {
        transform: translateX(0);
      }
    }

    .navigation {
      &--hidden {
        transform: translateX(-100%);
      }

      &__header__headline {
        > svg {
          display: none;
        }

        > h4 {
          padding-left: 0;
        }
      }
    }
  }
</style>
